import { clientPortalRequest, buildClientQuery } from "./clientPortalFetch";
import { API_BASE_URL, ensureCsrfToken } from "./apiClient";

function casamapaAdminBase(clientViewId) {
  return `/views/${clientViewId}/casamapa`;
}

export function fetchCasaMapaAdminDashboard(clientViewId) {
  return clientPortalRequest(`${casamapaAdminBase(clientViewId)}/dashboard`);
}

export function startCasaMapaAdminAgencyPortalSession(clientViewId, agencySlug) {
  return clientPortalRequest(`${casamapaAdminBase(clientViewId)}/agencies/${encodeURIComponent(agencySlug)}/portal-session`, {
    method: "POST",
  });
}

export function fetchCasaMapaAdminAgencies(clientViewId, params = {}) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/agencies${buildClientQuery(params)}`
  );
}

export function createCasaMapaAdminAgency(clientViewId, payload) {
  return clientPortalRequest(`${casamapaAdminBase(clientViewId)}/agencies`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function fetchCasaMapaAdminAgency(clientViewId, agencySlug) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/agencies/${encodeURIComponent(agencySlug)}`
  );
}

export function patchCasaMapaAdminAgencyCrm(clientViewId, agencySlug, payload) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/agencies/${encodeURIComponent(agencySlug)}/crm`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    }
  );
}

export function patchCasaMapaAdminAgencyProfile(clientViewId, agencySlug, payload) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/agencies/${encodeURIComponent(agencySlug)}/profile`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    }
  );
}

function agencyCrmBase(clientViewId, agencySlug) {
  return `${casamapaAdminBase(clientViewId)}/agencies/${encodeURIComponent(agencySlug)}/crm`;
}

export function postCasaMapaAdminAgencyCrmContact(clientViewId, agencySlug, payload) {
  return clientPortalRequest(`${agencyCrmBase(clientViewId, agencySlug)}/contacts`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function patchCasaMapaAdminAgencyCrmContact(clientViewId, agencySlug, contactId, payload) {
  return clientPortalRequest(
    `${agencyCrmBase(clientViewId, agencySlug)}/contacts/${encodeURIComponent(contactId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    }
  );
}

export function patchCasaMapaAdminAgencyCrmPrimaryContact(clientViewId, agencySlug, contactId) {
  return clientPortalRequest(`${agencyCrmBase(clientViewId, agencySlug)}/primary-contact`, {
    method: "PATCH",
    body: JSON.stringify({ contactId: contactId || null }),
  });
}

export function postCasaMapaAdminAgencyCrmAddress(clientViewId, agencySlug, payload) {
  return clientPortalRequest(`${agencyCrmBase(clientViewId, agencySlug)}/addresses`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function patchCasaMapaAdminAgencyCrmAddress(clientViewId, agencySlug, addressId, payload) {
  return clientPortalRequest(
    `${agencyCrmBase(clientViewId, agencySlug)}/addresses/${encodeURIComponent(addressId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    }
  );
}

export function deleteCasaMapaAdminAgencyCrmAddress(clientViewId, agencySlug, addressId) {
  return clientPortalRequest(
    `${agencyCrmBase(clientViewId, agencySlug)}/addresses/${encodeURIComponent(addressId)}`,
    { method: "DELETE" }
  );
}

export function postCasaMapaAdminAgencyCrmComment(clientViewId, agencySlug, payload) {
  return clientPortalRequest(`${agencyCrmBase(clientViewId, agencySlug)}/comments`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function uploadCasaMapaAdminAgencyCrmDocument(clientViewId, agencySlug, file) {
  const token = await ensureCsrfToken();
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(
    `${API_BASE_URL}/api/client/views/${clientViewId}/casamapa/agencies/${encodeURIComponent(agencySlug)}/crm/documents`,
    {
      method: "POST",
      headers: { "X-CSRF-Token": token },
      body: formData,
      credentials: "include",
    }
  );

  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(body?.message || body?.error || `Upload failed (${response.status})`);
  }
  return body;
}

export function deleteCasaMapaAdminAgencyCrmDocument(clientViewId, agencySlug, documentId) {
  return clientPortalRequest(
    `${agencyCrmBase(clientViewId, agencySlug)}/documents/${encodeURIComponent(documentId)}`,
    { method: "DELETE" }
  );
}

export function fetchCasaMapaAdminListingHealth(clientViewId, params = {}) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/listings/health${buildClientQuery(params)}`
  );
}

export function fetchCasaMapaAdminListing(clientViewId, listingId) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/listings/${encodeURIComponent(listingId)}`
  );
}

export function fetchCasaMapaAdminListingDataHealth(clientViewId, listingId) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/listings/${encodeURIComponent(listingId)}/data-health`
  );
}

export function fetchCasaMapaAdminListingSourceRuns(clientViewId, listingId, sourceKey, params = {}) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/listings/${encodeURIComponent(listingId)}/data-health/sources/${encodeURIComponent(sourceKey)}/runs${buildClientQuery(params)}`
  );
}

export function fetchCasaMapaAdminListingRunLogs(clientViewId, listingId, runId, params = {}) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/listings/${encodeURIComponent(listingId)}/data-health/runs/${encodeURIComponent(runId)}/logs${buildClientQuery(params)}`
  );
}

export function runCasaMapaAdminListingSource(clientViewId, listingId, sourceKey, body = {}) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/listings/${encodeURIComponent(listingId)}/data-health/sources/${encodeURIComponent(sourceKey)}/run`,
    {
      method: "POST",
      body: JSON.stringify(body),
    }
  );
}

export function patchCasaMapaAdminListing(clientViewId, listingId, payload) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/listings/${encodeURIComponent(listingId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    }
  );
}

export function fetchCasaMapaAdminListings(clientViewId, params = {}) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/listings${buildClientQuery(params)}`
  );
}

export function fetchCasaMapaAdminVendors(clientViewId, params = {}) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/vendors${buildClientQuery(params)}`
  );
}

export function fetchCasaMapaAdminLeads(clientViewId, params = {}) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/leads${buildClientQuery(params)}`
  );
}

export function fetchCasaMapaAdminLead(clientViewId, leadId) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/leads/${encodeURIComponent(leadId)}`
  );
}

export function patchCasaMapaAdminLeadStatus(clientViewId, leadId, partnerStatus) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/leads/${encodeURIComponent(leadId)}`,
    {
      method: "PATCH",
      body: JSON.stringify({ partnerStatus }),
    }
  );
}

export function addCasaMapaAdminLeadNote(clientViewId, leadId, noteText) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/leads/${encodeURIComponent(leadId)}/notes`,
    {
      method: "POST",
      body: JSON.stringify({ noteText }),
    }
  );
}

export function fetchCasaMapaPropertyLinking(clientViewId) {
  return clientPortalRequest(`${casamapaAdminBase(clientViewId)}/property-linking`);
}

export function resolveCasaMapaPropertyLinkMatch(clientViewId, matchCandidateId, action, notes) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/property-linking/matches/${encodeURIComponent(matchCandidateId)}`,
    {
      method: "POST",
      body: JSON.stringify({ action, notes }),
    }
  );
}

export function fetchCasaMapaPublicInventory(clientViewId, params = {}) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/public-inventory${buildClientQuery(params)}`
  );
}

export function updateCasaMapaPublicCriteria(clientViewId, payload) {
  return clientPortalRequest(`${casamapaAdminBase(clientViewId)}/public-inventory/criteria`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function setCasaMapaPublicListing(clientViewId, listingId, included) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/public-inventory/listings/${encodeURIComponent(listingId)}`,
    {
      method: "POST",
      body: JSON.stringify({ included }),
    }
  );
}

export function fetchCasaMapaPublicInventoryListingDetail(clientViewId, listingId) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/public-inventory/listings/${encodeURIComponent(listingId)}`
  );
}

export function createCasaMapaListingOperatorFlag(clientViewId, listingId, payload) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/public-inventory/listings/${encodeURIComponent(listingId)}/flags`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

export function resolveCasaMapaListingOperatorFlag(clientViewId, listingId, flagId, payload = {}) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/public-inventory/listings/${encodeURIComponent(listingId)}/flags/${encodeURIComponent(flagId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    }
  );
}

export function fetchCasaMapaMarketTrends(clientViewId, params = {}) {
  return clientPortalRequest(
    `${casamapaAdminBase(clientViewId)}/market-trends${buildClientQuery(params)}`
  );
}

export function createCasaMapaAdminLead(clientViewId, payload) {
  return clientPortalRequest(`${casamapaAdminBase(clientViewId)}/leads`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
